import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Wrench,
  ShieldCheck,
  Sparkles,
  Cog,
  Droplets,
  Snowflake,
  Cpu,
  KeyRound,
  Gauge,
  Settings2,
  Disc3,
  Phone,
  Mail,
  CheckCircle2,
  BookCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CaseStudies from "@/components/CaseStudies";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const HERO_IMG = "/gallery/exterior.jpg";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Márkadiagnosztika",
    text: "ISTA / Rheingold gyári rendszerrel — BMW és MINI hibakód-olvasás és javítás.",
  },
  {
    icon: Settings2,
    title: "Eredeti alkatrész",
    text: "OEM és gyári minőségű alkatrészek BMW és MINI modellekhez egyaránt.",
  },
  {
    icon: Sparkles,
    title: "BMW Group szenvedély",
    text: "BMW és MINI tulajdonosok kezében — az egész csapat rajongó.",
  },
];

const BMW_SERVICES = [
  {
    icon: Droplets,
    title: "Olajcsere — gyári előírás szerint",
    text:
      "BMW-specifikus motorolajok (LL-04, LL-01 FE), eredeti olajszűrő, leeresztőcsavar és tömítés. Az utántöltési értékeket az ISTA-ban resetel­jük.",
  },
  {
    icon: Cog,
    title: "Vezérműlánc-csere (N47, N57 + N14/N18)",
    text:
      "A jól ismert N47/N57 dízel és a MINI N14/N18 turbó-benzin motorok láncproblémájának teljes körű javítása — lánc, vezető­sín, feszítő, vízpumpa egyben.",
  },
  {
    icon: Gauge,
    title: "Vanos / Valvetronic javítás",
    text:
      "Vezérlésszabályozás-, Valvetronic motor- és Vanos-egység diagnosztika és csere. Tipikus N42/N46/N52 és újabb motorokon.",
  },
  {
    icon: Droplets,
    title: "ZF 6HP / 8HP automataváltó-olajcsere",
    text:
      "Dinamikus váltóolaj-csere MotulEVO géppel. Eredeti olaj, szűrőkád, tömítés — az adaptáció reset­elésével együtt.",
  },
  {
    icon: Disc3,
    title: "Fékrendszer — eredeti és sport",
    text:
      "Féktárcsa, fékbetét, fékfolyadék-csere. ATE, Textar, Brembo, M-Performance opciókkal.",
  },
  {
    icon: Wrench,
    title: "Futómű / légrugó (E70, F-széria)",
    text:
      "Teljes futómű-felújítás, légrugó-csere F01 / F07 / F11 / E70 modelleken. Szilent, lengéscsillapító, stabilizátor.",
  },
  {
    icon: Snowflake,
    title: "Klíma & hűtésrendszer",
    text:
      "Klímatöltés (R134a / R1234yf), kompresszor- és kondenzátor-csere. Vízpumpa, termosztát és hűtő javítása.",
  },
  {
    icon: Cpu,
    title: "Kódolás (ISTA / E-Sys)",
    text:
      "BMW és MINI retrofit-kódolás, rejtett funkciók aktiválása, NBT / EVO frissítés, FRM / FEM javítás és újra-kódolás.",
  },
  {
    icon: KeyRound,
    title: "Kulcsprogramozás",
    text:
      "Új kulcs / távirányító kódolása, elveszett kulcs zárolása CAS / FEM / BDC modulokon.",
  },
];

const SERIES = [
  { code: "BMW E-széria", desc: "E36 · E39 · E46 · E60 · E61 · E70 · E90 · E92" },
  { code: "BMW F-széria", desc: "F10 · F11 · F20 · F30 · F32 · F36 · F45 · F48" },
  { code: "BMW G-széria", desc: "G20 · G21 · G30 · G31 · G05 · G07 · G80 M3" },
  { code: "BMW M-modellek", desc: "M3 · M4 · M5 · M340i · M550i · X3M · X5M" },
  { code: "MINI klasszikus", desc: "R50 · R52 · R53 (W10 · W11 motorok)" },
  { code: "MINI 2. és 3. gen", desc: "R55 · R56 · R57 · R58 · R59 · R60 · R61 (N12/N14/N16/N18)" },
  { code: "MINI F-széria", desc: "F54 · F55 · F56 · F57 · F60 (B36 · B38 · B46 · B48)" },
  { code: "MINI JCW & Cooper S", desc: "John Cooper Works · Cooper S sport modellek" },
];

const PROCESS = [
  {
    n: "01",
    title: "Időpontfoglalás",
    text: "Online űrlapon vagy telefonon. 24 órán belül visszajelzünk.",
  },
  {
    n: "02",
    title: "Diagnosztika",
    text: "ISTA / Rheingold rendszerrel pontos hibakód-olvasás és kiértékelés.",
  },
  {
    n: "03",
    title: "Árajánlat",
    text: "Tételes, érthető árajánlat — csak az Ön jóváhagyásával dolgozunk.",
  },
  {
    n: "04",
    title: "Javítás & átadás",
    text: "Eredeti alkatrész, gyári előírás. Próbaút után dokumentált átadás.",
  },
];

export default function BmwPage() {
  useDocumentMeta({
    title:
      "BMW & MINI szervíz Debrecen | Autó-Szerelem — ISTA, MotulEVO, ZF olajcsere",
    description:
      "BMW és MINI szakszerviz Debrecenben — HIVATALOS online szervíz-vezetéssel és kulcsba történő bejegyzéssel. ISTA / Rheingold márkadiagnosztika, E-Sys kódolás, ZF 6HP/8HP olajcsere MotulEVO géppel, vezérműlánc-csere (N47, N57, N14/N18), Vanos/Valvetronic javítás, klíma, futómű — BMW E-, F-, G-szériák és MINI R55–F60 generációk egyaránt.",
    path: "/bmw",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="bmw-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-24 sm:pt-28 isolate" data-testid="bmw-hero-section">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Autó-Szerelem műhely versenyzői BMW-vel — Debrecen, Vadvirág utca 15/D"
            className="w-full h-full object-cover object-center opacity-45"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 65%, #0A0A0A 100%)",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-20 sm:pt-6 sm:pb-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-6 transition-colors"
            data-testid="bmw-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-8">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Külön részleg / BMW &amp; MINI
            </span>
          </div>

          <h1
            className="font-black uppercase text-white leading-[0.92] tracking-tight"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.1rem, 8vw, 6.5rem)",
            }}
            data-testid="bmw-headline"
          >
            <span className="text-[#0082D2]">BMW &amp; MINI</span>
            <br />
            szervíz Debrecenben.
          </h1>

          <p className="mt-8 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed">
            A BMW Group a mi szerelmünk. A Q-Service teljes körű javítása
            mellett külön szakterületként kezeljük a{" "}
            <span className="text-white font-semibold">BMW és a MINI</span>{" "}
            márkák szervízelését — közös márkadiagnosztikával, kódolással és
            online szervíz-vezetéssel. BMW E-, F-, G-szériák és MINI R55-től
            az F60-ig.
          </p>

          <div className="mt-10 flex flex-wrap gap-3" data-testid="bmw-hero-cta">
            <Link to="/#foglalas" className="btn-primary" data-testid="bmw-hero-booking-btn">
              Időpontfoglalás
            </Link>
            <a href="tel:+36308637476" className="btn-outline">
              <Phone className="w-4 h-4 mr-2" /> +36 30 863 7476
            </a>
          </div>

          <div className="mt-16 grid sm:grid-cols-3 gap-6 max-w-3xl">
            {PILLARS.map((p) => {
              const I = p.icon;
              return (
                <div key={p.title} className="border-l-2 border-[#0082D2] pl-4">
                  <I
                    className="w-6 h-6 text-[#0082D2] mb-3"
                    strokeWidth={1.5}
                  />
                  <div
                    className="text-base font-bold uppercase text-white tracking-tight"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {p.title}
                  </div>
                  <div className="text-sm text-zinc-400 mt-1">{p.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OFFICIAL BMW ONLINE SERVICE HISTORY — Premium credential strip */}
      <section
        className="relative border-t border-b border-[#0082D2]/30 py-10 sm:py-14"
        data-testid="bmw-online-service-strip"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,130,210,0.18) 0%, rgba(10,10,10,0.95) 50%, rgba(225,19,44,0.12) 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-2 flex justify-center lg:justify-start">
            <div className="relative">
              <div className="absolute inset-0 bg-[#0082D2]/30 blur-2xl" aria-hidden="true" />
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center bg-[#0A0A0A] border-2 border-[#0082D2]">
                <BookCheck
                  className="w-10 h-10 sm:w-12 sm:h-12 text-[#0082D2]"
                  strokeWidth={1.4}
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 text-center lg:text-left">
            <span
              className="text-[0.7rem] font-bold uppercase tracking-[0.32em] text-[#0082D2]"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Hivatalos partner státusz
            </span>
            <h3
              className="mt-2 font-black uppercase text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(1.5rem, 3.2vw, 2.4rem)",
              }}
            >
              Hivatalos{" "}
              <span className="text-[#0082D2]">BMW &amp; MINI online szervíz-vezetés</span>.
            </h3>
            <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Nem csak megjavítjuk az autóját — a szervíz tényét{" "}
              <span className="text-white font-semibold">
                közvetlenül beírjuk a gyári rendszerbe
              </span>{" "}
              (BMW és MINI is) és{" "}
              <span className="text-white font-semibold">a kulcsba is</span>.
              Ezzel a digitális szervízkönyve mindig hivatalos és naprakész —
              átadáskor pluszérték, márkaszervíz nélkül.
            </p>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-2.5 text-sm">
            {[
              "BMW + MINI gyári rendszerbe rögzítés",
              "Kulcsba történő bejegyzés",
              "Digitális szervízkönyv",
              "Átadáskor pluszérték",
            ].map((bullet) => (
              <div
                key={bullet}
                className="flex items-start gap-2.5 text-zinc-300"
              >
                <CheckCircle2
                  className="w-4 h-4 text-[#0082D2] flex-shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span>{bullet}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="bmw-why-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="section-eyebrow mb-4">A.</span>
            <h2
              className="mt-5 font-black uppercase text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
              }}
            >
              Miért hozzánk
              <br />
              hozza a <span className="text-[#0082D2]">BMW</span>-jét?
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-zinc-300 leading-relaxed">
            <p>
              A BMW nem egyszerű autó — egy filozófia, egy érzés.
              Tudjuk, mert mi is vezetünk és értünk hozzá. A megszokott szervíz­
              műveletek mellett ismerjük a BMW-specifikus problémákat is:
              az N47 láncproblémát, a Valvetronic kopását, az F-szériák
              vezérlésszabályzó akadozását, a légrugós futóművek tipikus
              hibáit.
            </p>
            <p>
              Gyári <strong className="text-white">ISTA / Rheingold</strong>{" "}
              rendszerrel dolgozunk, és kódoláshoz <strong className="text-white">E-Sys</strong>-t
              használunk. Eredeti alkatrészeket szerzünk be — vagy közösen
              eldöntjük az Önnek megfelelő minőségi alternatívát. Mindig
              átláthatóan és tételes ár­ajánlattal.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
              {[
                "Gyári BMW diagnosztika (ISTA / Rheingold)",
                "E-Sys kódolás, retrofit aktiválások",
                "Eredeti vagy kiváló minőségű alkatrész",
                "Tételes, érthető árajánlat",
                "Próbaút után dokumentált átadás",
                "Garanciális javítások (vállalt munkára)",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-zinc-300">
                  <CheckCircle2
                    className="w-4 h-4 text-[#0082D2] mt-0.5 shrink-0"
                    strokeWidth={2}
                  />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="bmw-services-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">B / Szolgáltatások</span>
              <h2
                className="mt-5 font-black uppercase text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                BMW-specifikus
                <br />
                <span className="text-zinc-500">szervízmunkák.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              A leggyakrabban kért BMW-szervízmunkák — a teljes lista a
              telefonos egyeztetésen finomítható.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {BMW_SERVICES.map((s, idx) => {
              const I = s.icon;
              return (
                <article
                  key={s.title}
                  className="bg-[#0e0e0e] hover:bg-[#141414] transition-colors p-7 flex flex-col"
                  data-testid={`bmw-service-${idx}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#0082D2]/10 border border-[#0082D2]/30">
                      <I
                        className="w-5 h-5 text-[#0082D2]"
                        strokeWidth={1.6}
                      />
                    </div>
                    <span
                      className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-zinc-600"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      0{idx + 1}
                    </span>
                  </div>
                  <h3
                    className="text-lg font-bold uppercase text-white leading-tight mb-2"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {s.text}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERIES */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="bmw-series-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="section-eyebrow mb-4">C / Modellek</span>
              <h2
                className="mt-5 font-black uppercase text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
                }}
              >
                Mely <span className="text-[#0082D2]">modellekkel</span>
                <br />
                foglalkozunk?
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                A BMW E-szériás klasszikusoktól a legújabb G-szériás
                M-modellekig — és a teljes MINI palettán (R50-től az F60-ig)
                minden generációban van tapasztalatunk.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
              {SERIES.map((s) => (
                <div
                  key={s.code}
                  className="bg-[#0e0e0e] p-6 flex items-start gap-4"
                  data-testid={`bmw-series-${s.code}`}
                >
                  <div className="m-stripe-vertical w-1 h-full min-h-[60px] shrink-0" />
                  <div>
                    <div
                      className="text-xl font-black uppercase text-white"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      {s.code}
                    </div>
                    <div className="text-sm text-zinc-400 mt-1 mono">
                      {s.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="bmw-process-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="section-eyebrow justify-center inline-flex">D / Folyamat</span>
            <h2
              className="mt-5 font-black uppercase text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
              }}
            >
              Az időpontfoglalástól
              <br />
              <span className="text-[#0082D2]">az átadásig.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PROCESS.map((p) => (
              <div
                key={p.n}
                className="bg-[#0e0e0e] p-7"
                data-testid={`bmw-process-${p.n}`}
              >
                <div
                  className="text-5xl font-black text-[#0082D2] leading-none"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.n}
                </div>
                <h3
                  className="mt-4 text-lg font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES — Munkáink motortípus szerint */}
      <CaseStudies />

      {/* CTA BANNER */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="bmw-cta-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#0d0d0d] to-[#0a0a0a] border border-white/10 p-10 sm:p-14 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#0082D2]/20 blur-3xl pointer-events-none" />
            <div className="m-stripe absolute top-0 left-0 right-0 h-1" />

            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <span
                  className="text-[0.7rem] font-bold uppercase tracking-[0.24em] text-[#0082D2]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Készen áll a BMW-je?
                </span>
                <h3
                  className="mt-3 font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                  }}
                >
                  Foglaljon időpontot
                  <br />
                  szakszerű BMW-szervízre.
                </h3>
                <p className="mt-4 text-zinc-400 max-w-xl">
                  Visszahívjuk Önt 24 órán belül a megerősített időponttal.
                  Tételes árajánlat, eredeti alkatrész — azzal a szenvedéllyel,
                  amit a BMW megérdemel.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link
                  to="/#foglalas"
                  className="btn-primary inline-flex items-center"
                  data-testid="bmw-cta-booking-btn"
                >
                  Időpontfoglalás <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
            <a
              href="tel:+36308637476"
              className="bg-[#0e0e0e] border border-white/5 hover:border-[#0082D2]/40 transition-colors p-5"
              data-testid="bmw-contact-phone"
            >
              <Phone
                className="w-5 h-5 text-[#0082D2] mx-auto mb-2"
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Telefon
              </div>
              <div className="text-white font-medium mt-1">+36 30 863 7476</div>
            </a>
            <a
              href="mailto:autoszerelem@gmail.com"
              className="bg-[#0e0e0e] border border-white/5 hover:border-[#0082D2]/40 transition-colors p-5"
              data-testid="bmw-contact-email"
            >
              <Mail
                className="w-5 h-5 text-[#0082D2] mx-auto mb-2"
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                E-mail
              </div>
              <div className="text-white font-medium mt-1">autoszerelem@gmail.com</div>
            </a>
            <div className="bg-[#0e0e0e] border border-white/5 p-5">
              <ShieldCheck
                className="w-5 h-5 text-[#0082D2] mx-auto mb-2"
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Cím
              </div>
              <div className="text-white font-medium mt-1">
                4031 Debrecen, Vadvirág u. 15/D
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
